const VALID_SCENARIO_STATUSES = new Set(['measured', 'no-timing', 'skipped', 'failed']);
const VALID_PROFILE_STATUSES = new Set(['completed', 'skipped', 'unavailable', 'failed']);
const VALID_COVERAGE_STATUSES = new Set(['measured', 'no-timing', 'skipped', 'unavailable', 'failed', 'not-found', 'budget-exhausted', 'untested']);
const VALID_PHASES = new Set(['initial', 'after-scroll']);

// A structural selector identifies a node, not its purpose. In particular,
// disclosure is not evidence that the measured action opened the main menu.
export function scenarioHumanLabel(scenario, language = 'en') {
  if (scenario.label) return scenario.label;
  const cs = language === 'cs';
  const labels = cs ? {
    disclosure: 'Otevření ovládacího prvku (konkrétní funkce neurčena)',
    search: 'Vyhledávání', video: 'Spuštění videa', gallery: 'Ovládání galerie',
    'image-enlargement': 'Zvětšení obrázku', tab: 'Přepnutí záložky'
  } : {
    disclosure: 'Open a control (specific function undetermined)',
    search: 'Search', video: 'Start video', gallery: 'Gallery interaction',
    'image-enlargement': 'Enlarge image', tab: 'Switch tab'
  };
  return labels[scenario.kind] || (cs ? 'Neurčený scénář' : 'Unknown scenario');
}

const finiteTiming = (value) => {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

const positiveDuration = (value) => {
  const timing = finiteTiming(value);
  return timing != null && timing > 0 ? timing : null;
};

const attemptsFor = (value, status) => {
  const attempts = Number(value);
  if (Number.isInteger(attempts) && attempts > 0) return attempts;
  return status === 'measured' ? 1 : null;
};

export const sanitizeDiagnosticScriptUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return '';
  const raw = value.trim();
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    parsed.username = '';
    parsed.password = '';
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString();
  } catch {
    if (/^(?:\/|\.\/|\.\.\/)[^<>\s]*$/.test(raw)) return raw.split(/[?#]/)[0];
    return '';
  }
};

export const normalizeInteractionDiagnostics = (value) => {
  if (!value || ![1, 2].includes(value.version) || !Array.isArray(value.scenarios)) return null;
  const scenarios = value.scenarios.map((scenario, index) => {
    // A legacy zero/missing duration is not evidence of a measured interaction.
    const status = scenario?.status === 'measured' && positiveDuration(scenario?.durationMs) == null
      ? 'no-timing'
      : VALID_SCENARIO_STATUSES.has(scenario?.status) ? scenario.status : 'failed';
    const executionDiagnostics = scenario?.executionDiagnostics;
    return {
      id: String(scenario?.id ?? index),
      kind: String(scenario?.kind ?? ''),
      label: String(scenario?.label ?? ''),
      status,
      reason: scenario?.reason == null ? '' : String(scenario.reason),
      executionDiagnostics: executionDiagnostics && typeof executionDiagnostics === 'object' ? {
        failureCode: typeof executionDiagnostics.failureCode === 'string' ? executionDiagnostics.failureCode : '',
        actionStage: typeof executionDiagnostics.actionStage === 'string' ? executionDiagnostics.actionStage : '',
        actionPhase: typeof executionDiagnostics.actionPhase === 'string' ? executionDiagnostics.actionPhase : '',
        error: executionDiagnostics.error && typeof executionDiagnostics.error === 'object' ? {
          classification: typeof executionDiagnostics.error.classification === 'string' ? executionDiagnostics.error.classification : '',
          message: typeof executionDiagnostics.error.message === 'string' ? executionDiagnostics.error.message : ''
        } : null
      } : null,
      phase: VALID_PHASES.has(scenario?.phase) ? scenario.phase : null,
      attempts: attemptsFor(scenario?.attempts, status),
      // A zero total duration is a legacy "not captured" sentinel. Component
      // timings may genuinely be zero and remain visible as such.
      durationMs: status === 'measured' ? positiveDuration(scenario?.durationMs) : null,
      inputDelayMs: status === 'measured' ? finiteTiming(scenario?.inputDelayMs) : null,
      processingMs: status === 'measured' ? finiteTiming(scenario?.processingMs) : null,
      presentationMs: status === 'measured' ? finiteTiming(scenario?.presentationMs) : null
    };
  });
  const coverage = Array.isArray(value.coverage) ? value.coverage.map((item, index) => {
    const status = VALID_COVERAGE_STATUSES.has(item?.status) ? item.status : 'unavailable';
    return {
      id: String(item?.id ?? `coverage-${index}`),
      kind: String(item?.kind ?? ''),
      label: String(item?.label ?? ''),
      status,
      reason: item?.reason == null ? '' : String(item.reason),
      phase: VALID_PHASES.has(item?.phase) ? item.phase : null,
      attempts: attemptsFor(item?.attempts, status),
      durationMs: status === 'measured' ? positiveDuration(item?.durationMs) : null
    };
  }) : [];
  const rawProfile = value.profile && typeof value.profile === 'object' ? value.profile : {};
  const profile = {
    status: VALID_PROFILE_STATUSES.has(rawProfile.status) ? rawProfile.status : 'unavailable',
    reason: rawProfile.reason == null ? '' : String(rawProfile.reason),
    trigger: rawProfile.trigger ? {
      attemptId: String(rawProfile.trigger.attemptId || ''),
      sameAsRepresentative: typeof rawProfile.trigger.sameAsRepresentative === 'boolean' ? rawProfile.trigger.sameAsRepresentative : null,
      value: finiteTiming(rawProfile.trigger.value),
      representativeValue: finiteTiming(rawProfile.trigger.representativeValue),
      scenarioDurationMs: finiteTiming(rawProfile.trigger.scenarioDurationMs)
    } : null,
    scenarioId: rawProfile.scenarioId == null ? '' : String(rawProfile.scenarioId),
    scenarioLabel: rawProfile.scenarioLabel == null ? '' : String(rawProfile.scenarioLabel),
    evidenceLevel: ['observed', 'candidate', 'unavailable'].includes(rawProfile.evidenceLevel)
      ? rawProfile.evidenceLevel
      : 'unavailable',
    durationMs: finiteTiming(rawProfile.durationMs),
    summary: rawProfile.summary && typeof rawProfile.summary === 'object' ? {
      scriptingMs: finiteTiming(rawProfile.summary.scriptingMs),
      layoutMs: finiteTiming(rawProfile.summary.layoutMs),
      paintMs: finiteTiming(rawProfile.summary.paintMs)
    } : null,
    scripts: Array.isArray(rawProfile.scripts) ? rawProfile.scripts.map(script => ({
      url: sanitizeDiagnosticScriptUrl(script?.url),
      functionName: script?.functionName == null ? '' : String(script.functionName),
      durationMs: finiteTiming(script?.durationMs)
    })).filter(script => script.url || script.functionName) : []
  };
  return {
    scenarios,
    coverage,
    pageUrl: value.pageUrl == null && value.url == null ? '' : String(value.pageUrl ?? value.url),
    profile
  };
};

export const scenarioCoverage = (diagnostics) => {
  const scenarios = diagnostics?.scenarios || [];
  return {
    measured: scenarios.filter(s => s.status === 'measured' && positiveDuration(s.durationMs) != null).length,
    total: scenarios.length,
    failed: scenarios.filter(s => s.status === 'failed').length,
    skipped: scenarios.filter(s => s.status === 'skipped').length,
    noTiming: scenarios.filter(s => s.status === 'no-timing' || (s.status === 'measured' && positiveDuration(s.durationMs) == null)).length
  };
};

const readableToken = (value, language, kind) => {
  const token = String(value || '').trim().slice(0, 80)
    .replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  if (language !== 'cs') return token;
  const translations = kind === 'stage' ? {
    'type input': 'psaní do pole',
    type: 'psaní',
    click: 'kliknutí',
    scroll: 'posun stránky',
    'find target': 'hledání prvku',
    preparation: 'příprava',
    validation: 'ověření prvku',
    focus: 'zaměření pole',
    typing: 'psaní do pole',
    observation: 'sledování odezvy',
    complete: 'dokončení'
  } : {
    'target unavailable': 'prvek není dostupný',
    'target missing': 'prvek nebyl nalezen',
    'action timeout': 'vypršel čas na akci',
    'input timeout': 'vypršel čas na vstup',
    'document replaced': 'stránka byla nahrazena'
  };
  return translations[token] || token;
};

const searchErrorCopy = (classification, language) => {
  const errors = language === 'cs' ? {
    timeout: 'akce vyhledávání překročila časový limit',
    detached: 'prvek vyhledávání nebo stránka byly odpojeny',
    'not-editable': 'do vyhledávacího pole nelze psát',
    'not-visible': 'vyhledávací pole není viditelné',
    unknown: 'akce vyhledávání selhala'
  } : {
    timeout: 'search action timed out',
    detached: 'search target or document detached',
    'not-editable': 'search target was not editable',
    'not-visible': 'search target was not visible',
    unknown: 'search action failed'
  };
  return errors[classification] || errors.unknown;
};

export const scenarioFailureDetail = (scenario, language = 'en') => {
  if (scenario?.status === 'measured') return '';
  const { failureCode, actionStage, actionPhase, error } = scenario?.executionDiagnostics || {};
  const cs = language === 'cs';
  return [
    error?.classification
      ? `${cs ? 'Důvod' : 'Reason'}: ${searchErrorCopy(error.classification, language)}`
      : failureCode && `${cs ? 'Důvod' : 'Reason'}: ${readableToken(failureCode, language, 'reason')}`,
    (actionPhase || actionStage) && `${cs ? 'Fáze akce' : 'Action stage'}: ${readableToken(actionPhase || actionStage, language, 'stage')}`
  ].filter(Boolean).join(' · ');
};

export const interactionDiagnosticCopy = (language = 'en') => {
  const cs = language === 'cs';
  return {
    title: cs ? 'Cílené interakční scénáře' : 'Targeted interaction scenarios',
    intro: cs
      ? 'Cílené čtenářské interakce v laboratorním běhu. Pokrytí neznamená kompletní test webu a hodnoty nejsou INP skutečných návštěvníků.'
      : 'Targeted reader interactions in a lab run. Coverage is not a comprehensive site test and values are not real-user INP.',
    scenarioCoverage: ({ measured, total, failed, skipped, noTiming }) => cs
      ? `Pokrytí scénářů: naměřeno ${measured}/${total} · selhalo ${failed} · přeskočeno ${skipped} · bez časování ${noTiming}. Toto nejsou platné opakované běhy; nezměřené scénáře nejsou rychlé.`
      : `Scenario coverage: measured ${measured}/${total} · failed ${failed} · skipped ${skipped} · no timing ${noTiming}. These are not valid repeat runs; unmeasured scenarios are not fast.`,
    page: cs ? 'Stránka' : 'Page',
    coverageTitle: cs ? 'Pokrytí čtenářských interakcí' : 'Reader interaction coverage',
    coverageIntro: cs
      ? 'Nenalezené nebo časovým rozpočtem omezené ovládací prvky jsou uvedeny jako neotestované, nikoli jako rychlé.'
      : 'Controls that were not found or exceeded the time budget are shown as untested, not fast.',
    scenario: cs ? 'Scénář' : 'Scenario',
    result: cs ? 'Výsledek' : 'Result',
    phase: cs ? 'Fáze' : 'Phase',
    attempts: cs ? 'Pokusy' : 'Attempts',
    duration: cs ? 'Trvání' : 'Duration',
    input: cs ? 'Vstup' : 'Input',
    processing: cs ? 'Zpracování' : 'Processing',
    presentation: cs ? 'Vykreslení' : 'Presentation',
    missing: cs ? '— bez měření' : '— no measurement',
    missingDetail: cs ? '— neuvedeno' : '— not recorded',
    status: {
      measured: cs ? 'naměřeno' : 'measured',
      'no-timing': cs ? 'bez časování' : 'no timing',
      skipped: cs ? 'přeskočeno' : 'skipped',
      failed: cs ? 'selhalo' : 'failed',
      unavailable: cs ? 'neotestováno' : 'untested',
      'not-found': cs ? 'ovládání nenalezeno' : 'control not found',
      'budget-exhausted': cs ? 'neotestováno — časový limit' : 'untested — time budget',
      untested: cs ? 'neotestováno' : 'untested'
    },
    phaseLabel: {
      initial: cs ? 'před posunem' : 'before scroll',
      'after-scroll': cs ? 'po posunu' : 'after scroll'
    },
    traceTitle: cs ? 'Samostatný diagnostický replay v Chrome DevTools' : 'Separate Chrome DevTools diagnostic replay',
    traceDisclaimer: cs
      ? 'Replay je pouze diagnostický: není hlavním laboratorním INP ani field INP. Kategorie timeline se nesčítají ani nepředstavují rozklad INP. Zahrnují pouze doložené události hlavního rámce v intervalu interakce, nikoli úplnou práci stránky.'
      : 'The replay is diagnostic only: it is neither the main lab INP nor field INP. Timeline categories are not summed or presented as an INP breakdown. They include only evidenced main-frame events within the interaction interval, not all page activity.',
    profileStatus: {
      completed: cs ? 'dokončen' : 'completed',
      skipped: cs ? 'přeskočen' : 'skipped',
      unavailable: cs ? 'nedostupný' : 'unavailable',
      failed: cs ? 'selhal' : 'failed'
    },
    replayScenario: cs ? 'Replay scénáře' : 'Replayed scenario',
    replayDuration: cs ? 'Odezva při replayi' : 'Replay interaction duration',
    evidence: cs ? 'Úroveň evidence' : 'Evidence level',
    evidenceLevel: {
      observed: cs ? 'pozorovaná' : 'observed',
      candidate: cs ? 'kandidátní' : 'candidate',
      unavailable: cs ? 'nedostupná' : 'unavailable'
    },
    categories: cs ? 'Kategorie diagnostické timeline' : 'Diagnostic timeline categories',
    scripting: 'Scripting',
    layout: 'Layout',
    paint: 'Paint',
    candidates: cs ? 'Kandidáti skriptů' : 'Script candidates',
    candidateWarning: cs
      ? 'Kandidátní evidence z replaye — nejde o prokázaného viníka. Časy mohou zahrnovat volané funkce a mezi skripty se překrývat; nesčítají se.'
      : 'Candidate evidence from the replay — not a proven culprit. Times can include callees and overlap across scripts; do not sum them.',
    unsupportedSource: cs ? 'nepodporovaný zdroj' : 'unsupported source'
  };
};

export const readerReason = (reason, language = 'en') => {
  if (language !== 'cs') return reason;
  return ({
    'No semantic gallery found': 'Nenalezeno podporované ovládání posouvání galerie uvnitř této stránky.',
    'No controlled image dialog found': 'Nenalezeno podporované ovládání zvětšení obrázku.',
    'No safe native video found': 'Nenalezeno podporované ovládání videa.',
    'Reader action awaiting execution': 'Ovládání nalezeno; akce zatím nebyla provedena.',
    'Reader control identity or state changed': 'Ovládání se před akcí změnilo; akce nebyla provedena.',
    'Player initialized but no unique Play control is available': 'Přehrávač se načetl, ale nepodařilo se jednoznačně určit tlačítko pro spuštění.',
    'Video playback confirmed by advancing media time': 'Přehrávání skutečně spuštěno a ověřeno postupem času videa.',
    'Video playback confirmed; no attributable Event Timing entry': 'Přehrávání skutečně spuštěno, ale časování interakce nebylo zachyceno.',
    'Video playback did not start or could not be confirmed': 'Přehrávání se nespustilo nebo jeho spuštění nebylo možné potvrdit.',
    'Video time did not advance before the observation deadline': 'Ovládání přehrávače bylo aktivováno, ale postup času videa se v časovém limitu nepotvrdil.',
    'Playback control did not become available before the observation deadline': 'Přehrávač v časovém limitu nezpřístupnil podporované tlačítko pro spuštění.',
    'Player initialization could not complete; a first-party script returned HTTP 403': 'Přehrávač se nepřipravil. Načtení skriptu webu bylo odmítnuto (HTTP 403).',
    'Player initialization could not complete; a first-party script failed to load': 'Přehrávač se nepřipravil. Na stránce se nepodařilo načíst skript webu.',
    'Image enlargement confirmed by active lightbox': 'Zvětšení obrázku skutečně otevřeno a ověřeno.',
    'Image enlargement confirmed; no attributable Event Timing entry': 'Zvětšení obrázku skutečně otevřeno, ale časování interakce nebylo zachyceno.',
    'Image enlargement did not open or could not be confirmed': 'Zvětšení obrázku se neotevřelo nebo nebylo možné otevření potvrdit.',
    'Image enlarged but could not be closed safely': 'Obrázek se zvětšil, ale jeho zavření se nepodařilo bezpečně potvrdit.',
    'Publisher lightbox requires a desktop viewport (min-width: 64rem)': 'Zvětšení obrázku je na tomto webu dostupné jen v desktopovém zobrazení (šířka od 64 rem); na mobilu se tato akce neprovádí.',
    'Gallery opened on a separate page; navigation is not INP': 'Galerie skutečně otevřena na samostatné stránce. Navigace není měřením INP původního článku.',
    'Gallery navigation could not be confirmed': 'Otevření galerie se nepodařilo potvrdit.',
    'Gallery navigation time budget exhausted': 'Otevření galerie nebylo otestováno kvůli zbývajícímu časovému limitu.'
  })[reason] || reason;
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const ms = (value, missing) => value == null ? missing : `${Math.round(value)} ms`;

export const replayTriggerNote = (profile, language = 'en') => {
  const trigger = profile?.trigger;
  if (!trigger || trigger.scenarioDurationMs == null) return '';
  const cs = language === 'cs';
  const identity = trigger.attemptId ? ` (${trigger.attemptId})` : '';
  return cs
    ? `Podnětem k profilování je původní interakce ${ms(trigger.scenarioDurationMs)}${identity}. ${trigger.sameAsRepresentative === false ? `Pochází z jiného pokusu než reprezentativní běh ${ms(trigger.representativeValue, 's neznámou hodnotou')}. ` : ''}Výsledek stránky ani medián se profilováním nemění. Časy a skripty níže pocházejí ze samostatného opakování, ne z původního pokusu.`
    : `Profiling was triggered by the original ${ms(trigger.scenarioDurationMs)} interaction${identity}. ${trigger.sameAsRepresentative === false ? `It belongs to a different attempt than the ${ms(trigger.representativeValue, 'unknown-value')} representative run. ` : ''}Profiling does not change the page result or median. Timings and scripts below come from a separate replay, not the original attempt.`;
};

export const replayReasonText = (profile, language = 'en') =>
  language === 'cs' && profile?.reason === 'Slow interaction did not repeat reliably'
    ? 'Pomalá interakce se při diagnostickém opakování nepotvrdila; její příčinu nelze z tohoto replaye určit.'
    : profile?.reason || '';

export const renderInteractionDiagnosticsHtml = (value, language = 'en', theme = 'dark') => {
  const diagnostics = normalizeInteractionDiagnostics(value);
  if (!diagnostics) return '';
  const copy = interactionDiagnosticCopy(language);
  const dark = theme !== 'paper';
  const card = dark
    ? 'background:#1e293b;border:1px solid #334155;border-radius:10px;padding:16px;margin:16px 0;'
    : 'border-top:1px solid #ddd;padding-top:22px;margin-top:24px;';
  const muted = dark ? '#94a3b8' : '#666';
  const text = dark ? '#e2e8f0' : '#222';
  const coverageRows = diagnostics.coverage.map(item => `
    <tr>
      <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};color:${text};">${escapeHtml(scenarioHumanLabel(item, language))}</td>
      <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};"><strong>${escapeHtml(copy.status[item.status])}</strong>${item.reason ? `<small style="display:block;color:${muted};">${escapeHtml(readerReason(item.reason, language))}</small>` : ''}</td>
      <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};">${item.phase ? escapeHtml(copy.phaseLabel[item.phase]) : copy.missingDetail}</td>
      <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};">${item.attempts == null ? copy.missingDetail : escapeHtml(item.attempts)}</td>
      <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};">${ms(item.durationMs, copy.missing)}</td>
    </tr>`).join('');
  const rows = diagnostics.scenarios.map(scenario => `
    <tr>
      <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};color:${text};">${escapeHtml(scenarioHumanLabel(scenario, language))}</td>
       <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};"><strong>${escapeHtml(copy.status[scenario.status])}</strong>${scenario.reason && !scenario.executionDiagnostics?.error?.classification ? `<small style="display:block;color:${muted};">${escapeHtml(readerReason(scenario.reason, language))}</small>` : ''}${scenarioFailureDetail(scenario, language) ? `<small style="display:block;color:${muted};">${escapeHtml(scenarioFailureDetail(scenario, language))}</small>` : ''}</td>
      <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};">${scenario.phase ? escapeHtml(copy.phaseLabel[scenario.phase]) : copy.missing}</td>
      <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};">${scenario.attempts == null ? copy.missing : escapeHtml(scenario.attempts)}</td>
      <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};">${ms(scenario.durationMs, copy.missing)}</td>
      <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};">${ms(scenario.inputDelayMs, copy.missing)}</td>
      <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};">${ms(scenario.processingMs, copy.missing)}</td>
      <td style="padding:7px;border-bottom:1px solid ${dark ? '#334155' : '#ddd'};">${ms(scenario.presentationMs, copy.missing)}</td>
    </tr>`).join('');
  const summary = diagnostics.profile.summary;
  const scriptRows = diagnostics.profile.scripts.map(script => `
    <li style="margin:4px 0;word-break:break-all;"><code>${escapeHtml(script.url || copy.unsupportedSource)}</code>${script.functionName ? ` · <code>${escapeHtml(script.functionName)}</code>` : ''}${script.durationMs == null ? '' : ` · ${ms(script.durationMs, copy.missing)}`}</li>`).join('');
  return `
  <section data-inp-interaction-diagnostics style="${card}">
    <h3 style="margin:0 0 6px;color:${text};">${escapeHtml(copy.title)}</h3>
    <p style="font-size:12px;color:${muted};margin:0 0 12px;">${escapeHtml(copy.intro)}</p>
    <p data-scenario-coverage style="font-size:12px;color:${text};margin:0 0 12px;">${escapeHtml(copy.scenarioCoverage(scenarioCoverage(diagnostics)))}</p>
    ${diagnostics.pageUrl ? `<p style="font-size:11px;color:${muted};margin:0 0 10px;word-break:break-all;"><strong>${escapeHtml(copy.page)}:</strong> ${escapeHtml(diagnostics.pageUrl)}</p>` : ''}
    ${coverageRows ? `<h4 style="margin:12px 0 5px;color:${text};">${escapeHtml(copy.coverageTitle)}</h4>
      <p style="font-size:11px;color:${muted};margin:0 0 8px;">${escapeHtml(copy.coverageIntro)}</p>
      <div style="overflow-x:auto;"><table style="width:100%;border-collapse:collapse;font-size:11px;">
        <thead><tr>${[copy.scenario, copy.result, copy.phase, copy.attempts, copy.duration].map(label => `<th style="padding:7px;text-align:left;color:${muted};">${escapeHtml(label)}</th>`).join('')}</tr></thead>
        <tbody>${coverageRows}</tbody>
      </table></div>` : ''}
    <div style="overflow-x:auto;"><table style="width:100%;border-collapse:collapse;font-size:11px;">
      <thead><tr>${[copy.scenario, copy.result, copy.phase, copy.attempts, copy.duration, copy.input, copy.processing, copy.presentation].map(label => `<th style="padding:7px;text-align:left;color:${muted};">${escapeHtml(label)}</th>`).join('')}</tr></thead>
      <tbody>${rows}</tbody>
    </table></div>
    <div style="margin-top:16px;padding-top:12px;border-top:1px solid ${dark ? '#334155' : '#ddd'};">
      <h4 style="margin:0 0 5px;color:${text};">${escapeHtml(copy.traceTitle)}</h4>
      <p style="font-size:11px;color:${muted};margin:0 0 8px;">${escapeHtml(copy.traceDisclaimer)}</p>
      <p style="font-size:12px;margin:0 0 6px;"><strong>${escapeHtml(copy.profileStatus[diagnostics.profile.status])}</strong>${diagnostics.profile.reason ? ` — ${escapeHtml(replayReasonText(diagnostics.profile, language))}` : ''}</p>
      ${replayTriggerNote(diagnostics.profile, language) ? `<p style="font-size:11px;color:${muted};">${escapeHtml(replayTriggerNote(diagnostics.profile, language))}</p>` : ''}
      <p style="font-size:11px;color:${muted};margin:0 0 8px;">${diagnostics.profile.scenarioLabel || diagnostics.profile.scenarioId ? `<strong>${escapeHtml(copy.replayScenario)}:</strong> ${escapeHtml(diagnostics.profile.scenarioLabel || diagnostics.profile.scenarioId)} · ` : ''}<strong>${escapeHtml(copy.replayDuration)}:</strong> ${ms(diagnostics.profile.durationMs, copy.missing)} · <strong>${escapeHtml(copy.evidence)}:</strong> ${escapeHtml(copy.evidenceLevel[diagnostics.profile.evidenceLevel])}</p>
      ${summary ? `<p style="font-size:11px;color:${muted};margin:0 0 8px;"><strong>${escapeHtml(copy.categories)}:</strong> ${copy.scripting} ${ms(summary.scriptingMs, copy.missing)} · ${copy.layout} ${ms(summary.layoutMs, copy.missing)} · ${copy.paint} ${ms(summary.paintMs, copy.missing)}</p>` : ''}
      ${scriptRows ? `<div style="font-size:11px;color:${muted};"><strong>${escapeHtml(copy.candidates)}</strong> — ${escapeHtml(copy.candidateWarning)}<ul style="margin:5px 0 0 18px;">${scriptRows}</ul></div>` : ''}
    </div>
  </section>`;
};

export const renderMultiPageInteractionDiagnosticsHtml = (pages, language = 'en', theme = 'dark') => {
  if (!Array.isArray(pages)) return '';
  return pages.map(page => {
    if (!page?.interactionDiagnostics) return '';
    return renderInteractionDiagnosticsHtml({
      ...page.interactionDiagnostics,
      pageUrl: page.url || page.interactionDiagnostics.pageUrl
    }, language, theme);
  }).join('');
};